import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { AlertDialog } from "@/widgets/AlertDailog";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { AddIndividualOfferForm, AddIndividualOfferSchema } from "@/shared/partnerschema";
import { useIndividualAddOffer, useIndividualEditOffer } from "@/hooks/useLocalStorage";

const OFFER_TYPE_OPTIONS = [
    { label: "Review", value: "review" },
    { label: "Instant", value: "instant" },
] as const;

function Field({
    label,
    required,
    error,
    children,
}: {
    label: string;
    required?: boolean;
    error?: string;
    children: React.ReactNode;
}) {
    return (
        <div className="space-y-1.5">
            <Label className="text-sm font-medium">
                {label}
                {required && <span className="text-red-500 ml-0.5">*</span>}
            </Label>
            {children}
            {error && <p className="text-red-500 text-xs">{error}</p>}
        </div>
    );
}

type AddIndividualOfferProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    initialData?: any | null;
};

export function AddIndividualOffer({
    open,
    onOpenChange,
    initialData,
}: AddIndividualOfferProps) {
    const isEdit = !!initialData;

    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [reqStatus, setReqStatus] = useState(false);

    const addMutation = useIndividualAddOffer();
    const editMutation = useIndividualEditOffer();

    const toDateInput = (val: string | Date | undefined): string => {
        if (!val) return "";

        // If it's already a "YYYY-MM-DD" string, return as-is
        if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
            return val;
        }

        const d = new Date(val);
        if (isNaN(d.getTime())) return "";

        // Use local date parts instead of UTC (avoids timezone shift)
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    const {
        register,
        handleSubmit,
        reset,
        watch,
        control,
        formState: { errors },
    } = useForm<AddIndividualOfferForm>({
        resolver: zodResolver(AddIndividualOfferSchema),
        defaultValues: {
            ExchangeOfferName: "",
            OfferType: "",
            OfferDetails: "",
            ValidFrom: undefined,
            ValidTo: undefined,
        },
    });

    const validFrom = watch("ValidFrom");

    useEffect(() => {
        if (!open) return;

        if (isEdit && initialData) {
            reset({
                ExchangeOfferName: initialData.ExchangeOfferName ?? "",
                OfferType: initialData.OfferType ?? "",
                OfferDetails: initialData.OfferDetails ?? "",
                // HTML date inputs expect "YYYY-MM-DD" strings; slice ISO timestamps if needed
                ValidFrom: toDateInput(initialData.ValidFrom),
                ValidTo: toDateInput(initialData.ValidTo),
            });
        } else {
            reset({
                ExchangeOfferName: "",
                OfferType: "",
                OfferDetails: "",
                ValidFrom: undefined,
                ValidTo: undefined,
            });
        }
    }, [open, initialData]);
    console.log("initial", initialData)
    const onSubmit = (data: AddIndividualOfferForm) => {
        if (isEdit) {
            editMutation.mutate({ ...data, Id: initialData.Id });
        } else {
            addMutation.mutate({ data });
        }
    };

    useEffect(() => {
        if (addMutation.isSuccess) {
            setReqStatus(true);
            setAlertMessage("Individual offer added successfully!");
            setAlertOpen(true);
            onOpenChange(false);
            addMutation.reset();
        } else if (addMutation.isError) {
            const raw = addMutation.error as Error;
            setReqStatus(false);
            setAlertMessage(parseApiErrorMessage(raw.message));
            setAlertOpen(true);
            onOpenChange(false);
            addMutation.reset();
        }
    }, [addMutation.isSuccess, addMutation.isError]);

    useEffect(() => {
        if (editMutation.isSuccess) {
            setReqStatus(true);
            setAlertMessage("Individual offer updated successfully!");
            setAlertOpen(true);
            onOpenChange(false);
            editMutation.reset();
        } else if (editMutation.isError) {
            const raw = editMutation.error as Error;
            setReqStatus(false);
            setAlertMessage(parseApiErrorMessage(raw.message));
            setAlertOpen(true);
            onOpenChange(false);
            editMutation.reset();
        }
    }, [editMutation.isSuccess, editMutation.isError]);

    const isPending = addMutation.isPending || editMutation.isPending;

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {isEdit ? "Edit Individual Offer" : "Add Individual Offer"}
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 pt-1">

                        <Field
                            label="Offer Name"
                            required
                            error={errors.ExchangeOfferName?.message}
                        >
                            <Input
                                placeholder="Enter offer name"
                                {...register("ExchangeOfferName")}
                            />
                        </Field>

                        <div className="grid grid-cols-1 gap-4">
                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Valid From" required error={errors.ValidFrom?.message}>
                                    <Input
                                        type="date"
                                        {...register("ValidFrom")}
                                        className="block"
                                    />
                                </Field>

                                <Field label="Valid To" required error={errors.ValidTo?.message}>
                                    <Input
                                        type="date"
                                        {...register("ValidTo")}
                                        className="block"
                                        min={validFrom ? String(validFrom) : undefined}  // disables all dates before ValidFrom
                                    />
                                </Field>
                            </div>

                        </div>

                        <Field
                            label="Offer Type"
                            required
                            error={errors.OfferType?.message}
                        >
                            <Controller
                                control={control}
                                name="OfferType"
                                render={({ field }) => (
                                    <Select
                                        value={field.value}
                                        onValueChange={field.onChange}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select type" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {OFFER_TYPE_OPTIONS.map((opt) => (
                                                <SelectItem key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </Field>

                        <Field
                            label="Offer Details"
                            error={errors.OfferDetails?.message}
                        >
                            <Textarea
                                placeholder="Enter offer details"
                                className="resize-none min-h-[90px]"
                                {...register("OfferDetails")}
                            />
                        </Field>

                        <div className="flex justify-end gap-2 pt-1">
                            <Button type="submit" disabled={isPending}>
                                {isPending ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        {isEdit ? "Updating…" : "Saving…"}
                                    </>
                                ) : isEdit ? (
                                    "Update"
                                ) : (
                                    "Save"
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isPending}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </>
    );
}